import React, { useEffect, useState } from 'react';
import { apiFetch, apiUrl } from '../services/api';

type Preferences = { background: boolean; proactive: boolean; paused: boolean; presence: boolean; timezone: string | null; quiet_start: number; quiet_end: number };
type Thought = { id: string; content: string; created_at: string };

export default function ReflectionPanel({ accountId, onPresence }: { accountId: string; onPresence: (value: boolean) => void }) {
  const [preferences, setPreferences] = useState<Preferences | null>(null);
  const [messages, setMessages] = useState<Thought[]>([]);
  const [status, setStatus] = useState('Connecting…');
  const [saving, setSaving] = useState(false);
  const [push, setPush] = useState('Closed-app notifications are not connected.');
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const received = new Set<string>();
    const receiveMessages = (rows: Thought[]) => {
      setMessages(Array.from(new Map<string, Thought>(rows.map(x => [x.id, x])).values()).reverse());
      for (const row of rows) if (!received.has(row.id)) {
        received.add(row.id);
        void apiFetch(`/reflection/messages/${encodeURIComponent(row.id)}/receipt`, { method: 'POST', signal: controller.signal }).catch(() => { received.delete(row.id); });
      }
    };
    const load = async () => {
      try {
        const [p, m] = await Promise.all([
          apiFetch('/reflection/preferences', { signal: controller.signal }),
          apiFetch('/reflection/messages', { signal: controller.signal }),
        ]);
        if (!p.ok || !m.ok) throw new Error('unavailable');
        const [prefs, inbox] = await Promise.all([p.json(), m.json()]);
        if (!active) return;
        setPreferences(prefs.preferences); onPresence(prefs.preferences.presence);
        // Server canonical IDs make reconnect and repeated deliveries idempotent.
        receiveMessages(inbox.messages);
        setStatus('Connected');
      } catch (e) { if (active) setStatus('Continuity unavailable — chat is still available.'); }
    };
    void load();
    const connection = typeof EventSource !== 'undefined' ? new EventSource(apiUrl('/reflection/events'), { withCredentials: true }) : null;
    if (connection) {
      connection.onmessage = event => {
        if (!active) return;
        try {
          const data = JSON.parse(event.data);
          receiveMessages(data.messages);
          setStatus('Connected');
        } catch { setStatus('Reconnecting…'); }
      };
      connection.addEventListener('degraded', () => { if (active) setStatus('Continuity unavailable — chat is still available.'); });
      connection.onerror = () => { if (active) setStatus('Reconnecting…'); };
    }
    // A device switches ownership only after discarding its old browser subscription.
    try { if ('serviceWorker' in navigator) {
      const oldAccount = window.localStorage.getItem('lexi-push-account');
      if (oldAccount && oldAccount !== accountId) {
        void navigator.serviceWorker.getRegistration('/').then(async reg => {
          const sub = await reg?.pushManager.getSubscription(); await sub?.unsubscribe();
        });
        window.localStorage.removeItem('lexi-push-account');
      }
    } } catch { /* Storage can be unavailable in private browsing. */ }
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void load(); }, 15000);
    const reconnect = () => { if (document.visibilityState === 'visible') void load(); };
    window.addEventListener('online', reconnect); document.addEventListener('visibilitychange', reconnect);
    return () => { active = false; connection?.close(); controller.abort(); clearInterval(timer); window.removeEventListener('online', reconnect); document.removeEventListener('visibilitychange', reconnect); onPresence(false); };
  }, [accountId, onPresence]);
  const change = async (values: Partial<Preferences>) => {
    setSaving(true);
    try {
      const response = await apiFetch('/reflection/preferences', { method: 'PUT', body: JSON.stringify(values) });
      if (!response.ok) throw new Error('save');
      const result = await response.json();
      setPreferences(result.preferences); onPresence(result.preferences.presence); setStatus('Saved');
    } catch { setStatus('Could not save. Your previous settings still apply.'); }
    finally { setSaving(false); }
  };
  return <section aria-label="Continuity and presence" style={{ padding: '8px 16px', borderBottom: '1px solid #987b9940' }}>
    <details>
      <summary>Hang out & continuity · <span role="status">{status}</span></summary>
      {preferences && <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: 8 }}>
        {([['presence', 'Hang out quietly'], ['background', 'Reflect between conversations'], ['proactive', 'Let Lexi start a conversation'], ['paused', 'Pause reflections and messages']] as const).map(([key, label]) =>
          <label key={key}><input type="checkbox" checked={preferences[key]} disabled={saving} onChange={e => void change({ [key]: e.target.checked, timezone: preferences.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone })} /> {label}</label>)}
        <label>Quiet hours start <input aria-label="Quiet hours start" type="number" min={0} max={23} value={preferences.quiet_start} disabled={saving} onChange={e => void change({ quiet_start: Number(e.target.value) })} style={{ width: 45 }} /></label>
        <label>Quiet hours end <input aria-label="Quiet hours end" type="number" min={0} max={23} value={preferences.quiet_end} disabled={saving} onChange={e => void change({ quiet_end: Number(e.target.value) })} style={{ width: 45 }} /></label>
        <span>{preferences.timezone || 'Timezone needed before unsolicited messages'}</span>
        <button disabled={saving} onClick={() => void change({ timezone: Intl.DateTimeFormat().resolvedOptions().timeZone })}>Use this device’s timezone</button>
        <button onClick={async () => {
          try {
            if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) { setPush('This browser does not support Web Push.'); return; }
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') { setPush('Notifications declined. Messages remain available here.'); return; }
            const configResponse = await apiFetch('/reflection/push/config');
            const config = await configResponse.json();
            if (!configResponse.ok || !config.available) { setPush('Browser permission granted; push transport is not configured. Messages remain available here.'); return; }
            const reg = await navigator.serviceWorker.register('/reflection-sw.js');
            await navigator.serviceWorker.ready;
            const key = config.public_key.replace(/-/g, '+').replace(/_/g, '/');
            const bytes = Uint8Array.from(atob(key + '='.repeat((4 - key.length % 4) % 4)), c => c.charCodeAt(0));
            const subscription = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: bytes });
            const saved = await apiFetch('/reflection/push/subscriptions', { method: 'POST', body: JSON.stringify(subscription.toJSON()) });
            if (!saved.ok) { await subscription.unsubscribe(); throw new Error('subscription'); }
            window.localStorage.setItem('lexi-push-account', accountId); setPush('Notifications enabled on this device.');
          } catch { setPush('Could not enable notifications. Messages remain available here.'); }
        }}>Enable device notifications</button>
        <button onClick={async () => {
          await apiFetch('/reflection/push/subscriptions', { method: 'DELETE' });
          if ('serviceWorker' in navigator) {
            const reg = await navigator.serviceWorker.getRegistration('/');
            const sub = await reg?.pushManager.getSubscription(); await sub?.unsubscribe();
          }
          window.localStorage.removeItem('lexi-push-account'); setPush('Device notifications disabled.');
        }}>Disable device notifications</button>
        <span>{push}</span>
        <p>Quiet presence keeps your selected avatar here without continuous generation. Reflections can be mistaken; you can correct them in chat. Unsolicited messages are limited to one per 24 hours.</p>
      </div>}
    </details>
    {preferences?.presence && <p>We can just hang out. No need to keep talking.</p>}
    <div aria-live="polite" aria-label="Messages from Lexi">
      {messages.slice(-5).map(m => <article key={m.id} data-message-id={m.id} style={{ padding: 8 }}><strong>Lexi</strong> <time dateTime={m.created_at}>{new Date(m.created_at).toLocaleString()}</time><p style={{ whiteSpace: 'pre-wrap' }}>{m.content}</p></article>)}
    </div>
  </section>;
}
